/**
 * Settings, Notifications: the nudge when nothing reaches the person outside the console (R-HL-2).
 * Shows with no enabled channel. "Turn on alerts" subscribes this browser to web push when the
 * hub can send it, so requests reach it with Orgus closed. Exported for the getting-started flow too.
 */
import { BellRing, Smartphone, TriangleAlert } from '@orgus/ui/icons';
import type { ApprovalExpiryPolicy, ApprovalsState, ChannelView } from '@orgus/protocol';
import { useState } from 'react';
import { Button, ErrorState, toUiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { usePwa } from '../mobile/pwa.ts';
import { defaultExpiryText, orgExpiry } from '../approvals/expiryModel.ts';
import { alertsOnToast, canPushHere, turnOnAlerts } from './NotifyFallback.tsx';
import { useNotifyState } from './api.ts';

/** The callout, without store reads: rendered in tests. `push`: the button subscribes to web push. */
export function NoChannelCallout({
  policy,
  permission,
  push = false,
  onAllow,
  onPush,
  error,
}: {
  policy: ApprovalExpiryPolicy;
  permission: NotificationPermission | 'unsupported';
  push?: boolean;
  onAllow: () => void;
  onPush: () => void;
  /** Turning on alerts failed: shown inline, with what, why and next. */
  error?: unknown;
}) {
  return (
    <div role="status" className="flex flex-col gap-2 rounded-card border border-waiting/40 bg-waiting-soft px-4 py-3">
      <p className="flex items-center gap-2 text-ui font-medium text-fg">
        <TriangleAlert className="size-4 shrink-0 text-waiting" aria-hidden />
        Nothing reaches you outside this console
      </p>
      <p className="text-caption text-fg-muted">
        {defaultExpiryText(policy)} Projects can set their own wait in Approval rules. Turn on push on your phone, add Slack or Teams, or at least turn on alerts in this browser.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {(permission === 'default' || (permission === 'granted' && push)) && (
          <Button size="sm" variant="secondary" icon={<BellRing />} onClick={onAllow}>
            {push ? 'Turn on alerts in this browser' : 'Allow alerts in this browser'}
          </Button>
        )}
        {permission === 'granted' && !push && <span className="text-caption text-passed">Alerts are on in this browser.</span>}
        <Button size="sm" variant="ghost" icon={<Smartphone />} onClick={onPush}>
          Turn on push
        </Button>
      </div>
      {error ? <ErrorState variant="inline" error={error} /> : null}
    </div>
  );
}

export function NoChannelNudge({ channels }: { channels: readonly Pick<ChannelView, 'enabled'>[] }) {
  const policy = orgExpiry(useOrgus((s) => s.ext.approvals as ApprovalsState | undefined));
  const mock = useOrgus((s) => s.mode === 'mock');
  const notify = useNotifyState();
  const [error, setError] = useState<unknown>(null);
  const permission = usePwa((s) => s.permission);
  usePwa((s) => `${s.endpoint}|${!!s.registration}`);
  if (channels.some((c) => c.enabled)) return null;
  const push = canPushHere(notify?.vapidPublicKey, mock);
  const allow = async () => {
    setError(null);
    try {
      alertsOnToast(await turnOnAlerts(notify?.vapidPublicKey, mock));
    } catch (e) {
      setError(toUiError(e, { what: 'Alerts were not turned on.' }));
    }
  };
  // "This device" is the first panel on the page.
  const scrollToPush = () => document.querySelector('main section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return <NoChannelCallout policy={policy} permission={permission} push={push} onAllow={() => void allow()} onPush={scrollToPush} error={error} />;
}
